import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';
import { FlowMetaContext } from '../FlowEditorContext';
import { HIGHLIGHT_OUTLINE } from '../editor-styles';
import { cn } from '../lib/utils';
import { FormItemIcon } from '../panels/items/FormItemIcon';
import { EntryHandle, NodeUnreachableTip, PreGreetingMark, TransitionList, } from './TransitionList';
/**
 * FormNode — the canvas card for a Form Node. Shows the block header plus a
 * compact list of items with inferred-metadata chips (id · type · required).
 * Mirrors the left panels of the authoring mockups.
 */
export function FormNode({ id, data, selected }) {
    const form = data;
    const [hovered, setHovered] = useState(false);
    const { activeNodeId, previousNodeId, edgeHighlightedNodeIds, reachable } = useContext(FlowMetaContext);
    const isActive = activeNodeId === id;
    const justLeft = previousNodeId === id && activeNodeId !== id;
    const activeShadow = [
        '0 0 0 2px rgba(34,197,94,0.55), 0 0 18px rgba(34,197,94,0.20)',
        '0 0 0 2px rgba(34,197,94,0.95), 0 0 28px rgba(34,197,94,0.42)',
        '0 0 0 2px rgba(34,197,94,0.55), 0 0 18px rgba(34,197,94,0.20)',
    ];
    const idleShadow = '0 0 0 0px rgba(34,197,94,0)';
    const visibleItems = form.items.slice(0, 3);
    const overflow = form.items.length - visibleItems.length;
    return (_jsx(NodeUnreachableTip, { nodeId: id, children: _jsxs(motion.div, { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), animate: {
                boxShadow: isActive ? activeShadow : idleShadow,
                scale: justLeft ? [1, 0.98, 1] : 1,
            }, transition: isActive
                ? { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }
                : justLeft
                    ? { duration: 0.35, ease: 'easeOut' }
                    : { duration: 0.25, ease: 'easeOut' }, className: cn('w-[260px] rounded-xl border-2 bg-card px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md', reachable.has(id)
                ? 'border-foreground'
                : 'border-dashed border-amber-500'), style: selected || edgeHighlightedNodeIds.has(id)
                ? HIGHLIGHT_OUTLINE
                : undefined, children: [_jsx(EntryHandle, { nodeId: id }), _jsxs("div", { className: "flex items-center gap-2 min-w-0", children: [_jsx(ClipboardList, { className: "h-3.5 w-3.5 text-primary flex-shrink-0" }), _jsx("span", { className: "text-sm font-medium truncate", children: form.title || 'New form' }), _jsx(PreGreetingMark, { nodeId: id })] }), _jsxs("ol", { className: "mt-2 space-y-1", children: [visibleItems.map((item, i) => (_jsxs("li", { className: "flex items-start gap-1.5 text-xs text-foreground", children: [_jsxs("span", { className: "text-[10px] font-medium text-muted-foreground mt-0.5", children: [i + 1, "."] }), _jsx(FormItemIcon, { item: item, className: "mt-0.5" }), _jsx("span", { className: "line-clamp-2", children: item.text })] }, item.id))), overflow > 0 && (_jsxs("li", { className: "text-[10px] text-muted-foreground italic", children: ["+", overflow, " more"] }))] }), _jsx(TransitionList, { nodeId: id, hovered: hovered })] }) }));
}
//# sourceMappingURL=FormNode.js.map